import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { pacsAPI, patientAPI } from '../../utils/api';
import PatientLookup from '../../components/shared/PatientLookup';
import { printRadiologyOrderSlip } from '../../utils/printSlip';

const inputStyle = { width: '100%', padding: '9px 12px', border: '1px solid #d1d5db', borderRadius: 7, fontSize: 13, boxSizing: 'border-box' };

const MODALITY_LABELS = { XRAY: 'X-Ray', CT: 'CT Scan', MRI: 'MRI', USG: 'Ultrasound', MAMMO: 'Mammography', FLUORO: 'Fluoroscopy' };

export default function PACSNewOrder() {
    const location = useLocation();
    const [patient, setPatient] = useState(null);
    const [tests, setTests] = useState([]);
    const [selectedTest, setSelectedTest] = useState(null); // full test object
    const [form, setForm] = useState({ source: 'OPD', priority: 'Routine', modality: '', test_id: '', clinical_indication: '', is_contrast: false });
    const [loading, setLoading] = useState(false);
    const [done, setDone] = useState(null);
    const [lastOrder, setLastOrder] = useState(null);
    const patientRef = useRef(null);

    const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

    useEffect(() => {
        if (location.state?.patient_id) {
            patientAPI.getById(location.state.patient_id).then(r => {
                setPatient(r.data);
                set('source', 'IPD / Admitted');
            }).catch(() => {});
        }
    }, []);

    useEffect(() => {
        if (form.modality) {
            pacsAPI.getTests(form.modality).then(r => setTests(r.data)).catch(() => setTests([]));
        } else { setTests([]); }
        set('test_id', '');
        setSelectedTest(null);
    }, [form.modality]);

    const handleTestChange = (e) => {
        const id = e.target.value;
        set('test_id', id);
        const t = tests.find(t => String(t.id) === String(id));
        setSelectedTest(t || null);
    };

    const createOrder = async (confirmedPatient) => {
        if (!form.modality) return toast.error('Please select modality');
        setLoading(true);
        try {
            const res = await pacsAPI.createOrder({ ...form, patient_id: confirmedPatient.id });
            toast.success(`Radiology order created: ${res.data.order_number}`);

            const modalityLabel = MODALITY_LABELS[form.modality] || form.modality;
            const testList = selectedTest
                ? [{ test_name: selectedTest.test_name, modality: modalityLabel, body_part: selectedTest.body_part || '' }]
                : [{ test_name: modalityLabel, modality: modalityLabel, body_part: '' }];

            const orderDetails = { priority: form.priority, source: form.source, clinical_info: form.clinical_indication };
            printRadiologyOrderSlip(confirmedPatient, res.data.order_number, testList, orderDetails);
            setLastOrder({ orderNumber: res.data.order_number, tests: testList, details: orderDetails });
            setDone({ patient: confirmedPatient, orderNumber: res.data.order_number });
            setForm({ source: 'OPD', priority: 'Routine', modality: '', test_id: '', clinical_indication: '', is_contrast: false });
            setSelectedTest(null);
            setPatient(null);
        } catch (err) {
            toast.error(err.response?.data?.message || 'Failed to create order');
        } finally { setLoading(false); }
    };

    return (
        <div style={{ maxWidth: 800 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
                <span style={{ fontSize: 32 }}>🩻</span>
                <div>
                    <h2 style={{ fontSize: 20, fontWeight: 700, color: '#0369a1', margin: 0 }}>Radiology Order</h2>
                    <p style={{ fontSize: 12, color: '#64748b', margin: 0 }}>Radiology — New Imaging Order</p>
                </div>
            </div>

            {done && (
                <div style={{ background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, padding: 16, marginBottom: 20 }}>
                    <p style={{ color: '#1d4ed8', fontWeight: 700, margin: 0 }}>✅ Radiology Order Created!</p>
                    <p style={{ color: '#1d4ed8', fontSize: 14, margin: '4px 0 0' }}>
                        Order: <strong>{done.orderNumber}</strong> | {done.patient.full_name} ({done.patient.mrn})
                    </p>
                </div>
            )}

            {/* 1 - Patient Details */}
            <PatientLookup ref={patientRef} onPatientReady={setPatient} department="Radiology" hideButton onAfterRegister={createOrder}
                onReprint={lastOrder ? (p) => printRadiologyOrderSlip(p, lastOrder.orderNumber, lastOrder.tests, lastOrder.details) : null} />

            {/* 2 - Order Details */}
            <div style={{ background: 'white', borderRadius: 10, padding: 24, marginBottom: 16, border: '1px solid #e2e8f0' }}>
                <h3 style={{ fontSize: 13, fontWeight: 700, color: '#0369a1', marginBottom: 16 }}>Order Details</h3>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 14, marginBottom: 14 }}>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Source</label>
                        <select style={{ ...inputStyle, background: 'white' }} value={form.source} onChange={e => set('source', e.target.value)}>
                            <option>OPD</option><option>ER</option><option>IPD</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Priority</label>
                        <select style={{ ...inputStyle, background: 'white' }} value={form.priority} onChange={e => set('priority', e.target.value)}>
                            <option>STAT</option><option>Urgent</option><option>Routine</option>
                        </select>
                    </div>
                    <div>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Modality *</label>
                        <select style={{ ...inputStyle, background: 'white' }} value={form.modality} onChange={e => set('modality', e.target.value)}>
                            <option value="">Select Modality</option>
                            <option value="XRAY">X-Ray</option>
                            <option value="CT">CT Scan</option>
                            <option value="MRI">MRI</option>
                            <option value="USG">Ultrasound</option>
                            <option value="MAMMO">Mammography</option>
                            <option value="FLUORO">Fluoroscopy</option>
                        </select>
                    </div>
                </div>

                {tests.length > 0 && (
                    <div style={{ marginBottom: 14 }}>
                        <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>
                            Exam Type {selectedTest && <span style={{ color: '#059669', fontWeight: 700 }}>✓ {selectedTest.test_name}</span>}
                        </label>
                        <select style={{ ...inputStyle, background: 'white' }} value={form.test_id} onChange={handleTestChange}>
                            <option value="">-- Select Exam --</option>
                            {tests.map(t => <option key={t.id} value={t.id}>{t.test_name}{t.body_part ? ` — ${t.body_part}` : ''}</option>)}
                        </select>
                    </div>
                )}

                <div style={{ marginBottom: 14 }}>
                    <label style={{ fontSize: 11, fontWeight: 600, display: 'block', marginBottom: 4 }}>Clinical Indication</label>
                    <textarea style={{ ...inputStyle, height: 70 }} value={form.clinical_indication}
                        onChange={e => set('clinical_indication', e.target.value)}
                        placeholder="Reason for exam, clinical findings, suspected diagnosis..." />
                </div>

                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                    <input type="checkbox" checked={form.is_contrast} onChange={e => set('is_contrast', e.target.checked)} style={{ width: 16, height: 16 }} />
                    <span style={{ fontSize: 13, fontWeight: 600 }}>Contrast Study Required</span>
                    {form.is_contrast && <span style={{ fontSize: 12, color: '#f97316' }}>Consent form required</span>}
                </label>
            </div>

            {/* Green button last */}
            <button type="button" disabled={loading}
                onClick={() => patient ? createOrder(patient) : patientRef.current?.triggerRegister()}
                style={{ padding: '12px 32px', background: loading ? '#9ca3af' : '#059669', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 600, cursor: 'pointer' }}>
                {loading ? 'Processing...' : '✅ Confirm Patient & Continue'}
            </button>
        </div>
    );
}
